'use client';

/**
 * DetailPage scaffold — Phase A2.
 *
 * Two-column body wrapper that every detail page should render through.
 * The header sits on top, the body is a `1fr + 320px` grid (main
 * content + sticky right rail) that collapses to a single column below
 * `lg` (1024px).
 *
 * Spec: `UX_PRODUCTION_ROADMAP.md` §A2. Reference implementation is the
 * Phase 5 incident-detail v2 (`apps/web/src/components/nav-v2/
 * incident-detail/incident-detail-v2.tsx`) — moving its layout primitives
 * into the design system so client / care-plan / staff detail pages
 * can compose the same shape.
 *
 * Owned-by-scaffold:
 * - Outer spacing + responsive grid
 * - PageHeader for opted-in pages, or legacy DetailPageHeader
 * - Sticky bottom action bar slot (appears when in edit mode)
 *
 * Owned-by-page:
 * - Header content (title, status, context, actions)
 * - Body sections (tabs, cards, forms, timelines)
 * - Right-rail content (details panel, assignment, related records, audit)
 */

import React from 'react';
import { cn } from '../../lib/utils';
import { ExperienceRecordEvidence } from '../experience-record-evidence';
import { PageHeader, type PageHeaderProps } from '../page-header';
import { BannerSlot } from './banner';
import { DetailPageHeader, type DetailPageHeaderProps } from './detail-page-header';

export interface DetailPageProps extends Omit<DetailPageHeaderProps, 'className' | 'title'> {
  /** Shared record or case header; takes precedence over the legacy hero. */
  pageHeader?: PageHeaderProps;
  /** @deprecated Use pageHeader.title. */
  title?: string;
  /** @deprecated Use pageHeader.breadcrumbs. */
  backHref?: string;
  /** @deprecated Use pageHeader.breadcrumbs. */
  backLabel?: string;
  /** @deprecated Use pageHeader.breadcrumbs. */
  backLinkRender?: DetailPageHeaderProps['backLinkRender'];
  /** @deprecated Use pageHeader.breadcrumbs. */
  backLinkClassName?: string;
  /** @deprecated Use pageHeader.meta. */
  eyebrow?: React.ReactNode;
  /** @deprecated Use pageHeader.status. */
  statusPills?: React.ReactNode;
  /** @deprecated Use pageHeader.actions. */
  actions?: React.ReactNode;
  /** @deprecated Use pageHeader for header context. */
  presence?: React.ReactNode;
  /** @deprecated Use pageHeader.actions. */
  share?: React.ReactNode;
  /** Existing domain header, retaining its status, deadlines and action semantics. */
  header?: React.ReactNode;
  /** Main body content (left column on desktop, full-width on mobile). */
  children: React.ReactNode;
  /** Optional right rail content (sticky on desktop, stacks below on mobile). */
  rail?: React.ReactNode;
  /** Accessible rail name; bridged pages default to related facts and actions. */
  railLabel?: string;
  /** Width of the right rail. Defaults to 320px. */
  railWidth?: number;
  /** Sticky bottom action bar — appears only when this prop is non-null. */
  stickyBottomBar?: React.ReactNode;
  /** Override the outer wrapper class. */
  className?: string;
  /** Override the default header border + background. */
  headerClassName?: string;
  /** Exact rendered record/relationship/outcome evidence for provider-runtime certification. */
  experienceEvidence?: {
    entityIds: readonly string[];
    relationshipIds?: readonly string[];
    outcomeTokens?: readonly string[];
    revision: string;
  };
}

export function DetailPage({
  // header
  header,
  pageHeader,
  backHref,
  backLabel,
  backLinkRender,
  backLinkClassName,
  eyebrow,
  title,
  statusPills,
  actions,
  banner,
  presence,
  share,
  // body
  children,
  rail,
  railLabel,
  railWidth = 320,
  stickyBottomBar,
  className,
  headerClassName,
  experienceEvidence,
}: DetailPageProps) {
  return (
    <div className={cn('flex flex-col gap-4 pb-16', className)} data-detail-page>
      {experienceEvidence ? <ExperienceRecordEvidence {...experienceEvidence} /> : null}
      {header ??
        (pageHeader ? (
          <>
            <PageHeader {...pageHeader} className={cn(pageHeader.className, headerClassName)} />
            <BannerSlot record={banner} />
          </>
        ) : (
          <DetailPageHeader
            backHref={backHref}
            backLabel={backLabel}
            backLinkRender={backLinkRender}
            backLinkClassName={backLinkClassName}
            eyebrow={eyebrow}
            title={title ?? ''}
            statusPills={statusPills}
            actions={actions}
            banner={banner}
            presence={presence}
            share={share}
            className={headerClassName}
          />
        ))}

      {rail ? (
        <div
          className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_var(--detail-rail-width)]"
          style={{ ['--detail-rail-width' as string]: `${railWidth}px` }}
        >
          <div className="min-w-0 space-y-4">{children}</div>
          <aside
            className="min-w-0 space-y-4 lg:sticky lg:top-4 lg:self-start"
            aria-label={railLabel ?? (pageHeader ? 'Related facts and actions' : 'Right rail')}
          >
            {rail}
          </aside>
        </div>
      ) : (
        <div className="min-w-0 space-y-4">{children}</div>
      )}

      {stickyBottomBar && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-surface-sheet/95 px-4 py-3 shadow-sheet backdrop-blur sm:px-6"
          role="region"
          aria-label="Page actions"
        >
          <div className="min-w-0 max-w-full overflow-x-auto">{stickyBottomBar}</div>
        </div>
      )}
    </div>
  );
}
